import type { NodeDocContent } from "./nodeDocsData";

/**
 * Docs for nodes that exist in the engine (see workflow/nodes/index.ts) but
 * were missing from the reference: Schedule, Web Scraper, Validator,
 * Error Catcher, DocxIQ Export, PostgreSQL.
 */
export const additionalNodesDocs: Record<string, NodeDocContent> = {
  SCHEDULE: {
    title: "Schedule Trigger",
    subtitle: "Time-based Start",
    color: "amber",
    badge: "Trigger",
    description:
      "Starts the workflow automatically on a recurring schedule — every few minutes, hourly, daily, or on a custom cron expression. Use it for unattended jobs like nightly imports or periodic reports.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          The Schedule node runs your workflow on a timer using the external scheduler service — no one needs to press "Run".
        </div>

        <section>
          <h2>How it works</h2>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Pick an interval</p>
                <p class="wf-step-desc">Choose minutes, hours, days, or a raw cron expression, plus a timezone.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Register the job</p>
                <p class="wf-step-desc">Saving the workflow registers the schedule with the scheduler service.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Runs automatically</p>
                <p class="wf-step-desc">On each tick the scheduler triggers the workflow from this node.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead><tr><th>Parameter</th><th>Description</th><th>Required</th></tr></thead>
            <tbody>
              <tr><td><code>Interval</code></td><td>Run every N minutes / hours / days, or switch to a custom cron expression.</td><td>Yes</td></tr>
              <tr><td><code>Cron expression</code></td><td>Standard 5-field cron (e.g. <code>*/5 * * * *</code>) when using custom mode.</td><td>No</td></tr>
              <tr><td><code>Timezone</code></td><td>Timezone the schedule is evaluated in (e.g. Asia/Kolkata).</td><td>Yes</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>Nightly imports</strong> — pull and process new documents every night.</li>
            <li><strong>Periodic reports</strong> — generate and email a report on a daily or weekly cadence.</li>
            <li><strong>Polling</strong> — re-check a source on a fixed interval.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>A Schedule node should be the first node in the workflow.</li>
            <li>Keep intervals reasonable — very frequent runs consume resources and AI tokens.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "WEB-SCRAPER": {
    title: "Web Scraper",
    subtitle: "Crawl & Ingest Web Content",
    color: "indigo",
    badge: "Action",
    description:
      "Crawls a website starting from a seed URL and ingests the pages (and optionally linked files) so they can be processed downstream. Respects robots.txt and supports change detection.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Point the scraper at a starting URL and it discovers and captures pages for you — ideal for turning a website into searchable documents.
        </div>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead><tr><th>Parameter</th><th>Description</th><th>Required</th></tr></thead>
            <tbody>
              <tr><td><code>Seed URL</code></td><td>The page the crawl starts from.</td><td>Yes</td></tr>
              <tr><td><code>Allowed file types</code></td><td>Restrict which linked file types are downloaded (e.g. pdf, docx).</td><td>No</td></tr>
              <tr><td><code>Respect robots.txt</code></td><td>Honor the site's crawl rules. Recommended on.</td><td>No</td></tr>
              <tr><td><code>Crawl subdomains</code></td><td>Follow links into subdomains of the seed host.</td><td>No</td></tr>
              <tr><td><code>Playwright fallback</code></td><td>Render JavaScript-heavy pages with a headless browser when needed.</td><td>No</td></tr>
              <tr><td><code>Change detection</code></td><td>Only ingest pages that changed since the last crawl.</td><td>No</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>Knowledge base ingestion</strong> — capture a docs site into your document store.</li>
            <li><strong>Competitive monitoring</strong> — re-crawl on a schedule and detect changes.</li>
            <li><strong>Document harvesting</strong> — collect PDFs linked across a site.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Leave <code>Respect robots.txt</code> on for public sites.</li>
            <li>Pair with a Schedule trigger + change detection for ongoing sync.</li>
          </ul>
        </div>
      </div>
    `,
  },

  VALIDATOR: {
    title: "Validator",
    subtitle: "AI Quality Gate",
    color: "violet",
    badge: "Conditional",
    description:
      "Checks structured data against an instruction and iteratively corrects it until it passes (up to a retry limit). Use it as a quality gate before exporting or acting on extracted data.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          The Validator re-checks data against your rules and attempts to fix issues automatically, so downstream nodes only receive clean output.
        </div>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead><tr><th>Parameter</th><th>Description</th><th>Required</th></tr></thead>
            <tbody>
              <tr><td><code>Input JSON</code></td><td>The data to validate — map it from an upstream node's output.</td><td>Yes</td></tr>
              <tr><td><code>Instruction</code></td><td>Plain-language rules the data must satisfy (e.g. "every invoice must have a total and a date").</td><td>Yes</td></tr>
              <tr><td><code>Iteration limit</code></td><td>Maximum correction attempts before giving up (default 3).</td><td>No</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>Field completeness</strong> — ensure required fields are present before saving.</li>
            <li><strong>Format enforcement</strong> — confirm dates, currencies, or IDs match the expected shape.</li>
            <li><strong>Self-healing extraction</strong> — let the AI retry until the output is valid.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Write specific, testable instructions for reliable validation.</li>
            <li>Keep the iteration limit modest to control token usage.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "ERROR-CATCHER": {
    title: "Error Catcher",
    subtitle: "Failure Handling",
    color: "red",
    badge: "Action",
    description:
      "Catches errors from connected nodes so the workflow can handle failures gracefully — log them, notify someone, or run a fallback path — instead of stopping entirely.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Connect a node's error output to the Error Catcher to define what happens when that node fails.
        </div>

        <section>
          <h2>How it works</h2>
          <p>Most nodes expose an error branch. Wiring that branch into the Error Catcher routes failures here, where you can continue the workflow down a recovery path instead of aborting the whole run.</p>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>Notify on failure</strong> — send an email/webhook when a step errors.</li>
            <li><strong>Fallback path</strong> — try an alternate node when the primary one fails.</li>
            <li><strong>Resilient batches</strong> — keep processing remaining items when one fails.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Connect the <em>error</em> handle (not the normal output) of the node you want to guard.</li>
            <li>Pair with a notification node to get alerted on failures.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "DOCXIQ-EXPORT": {
    title: "DocxIQ Export",
    subtitle: "Save to Document Store",
    color: "emerald",
    badge: "Export",
    description:
      "Saves a file, a file URL, or generated text content into a DocxIQ document store (and folder), running it through AI/OCR processing so it becomes searchable alongside your other documents.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Use DocxIQ Export to land workflow output back into a document store — closing the loop so processed data is searchable in the app.
        </div>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead><tr><th>Parameter</th><th>Description</th><th>Required</th></tr></thead>
            <tbody>
              <tr><td><code>Document Store ID</code></td><td>Target store the document is saved into.</td><td>Yes</td></tr>
              <tr><td><code>Folder ID</code></td><td>Optional folder within the store (defaults to root).</td><td>No</td></tr>
              <tr><td><code>File / File URL / Text</code></td><td>What to save — an uploaded file object, a downloadable URL, or generated text content.</td><td>Yes</td></tr>
              <tr><td><code>AI performance level</code></td><td>LOW / MEDIUM / HIGH — quality vs. speed/cost of processing.</td><td>No</td></tr>
              <tr><td><code>File processing type</code></td><td>How the document is parsed (e.g. AI_OCR).</td><td>No</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>Persist results</strong> — save a generated PDF/summary back into a store.</li>
            <li><strong>Re-ingest</strong> — push files fetched from an API or scrape into DocxIQ.</li>
            <li><strong>Pipeline output</strong> — make workflow output searchable for end users.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Provide exactly one source — file object, file URL, or text content.</li>
            <li>Use MEDIUM performance for a good quality/cost balance.</li>
          </ul>
        </div>
      </div>
    `,
  },

  POSTGRES: {
    title: "PostgreSQL",
    subtitle: "Relational Database",
    color: "sky",
    badge: "Export",
    description:
      "Reads from or writes to a PostgreSQL database — query rows to feed the workflow, or insert/update processed data into a table. Connection details are provided per node.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Connect a workflow to your PostgreSQL database to pull source data or persist results into a table.
        </div>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead><tr><th>Parameter</th><th>Description</th><th>Required</th></tr></thead>
            <tbody>
              <tr><td><code>Connection</code></td><td>Host, port, database, user, password, and SSL toggle.</td><td>Yes</td></tr>
              <tr><td><code>Operation</code></td><td>SELECT / INSERT / UPDATE — what the node does.</td><td>Yes</td></tr>
              <tr><td><code>Schema &amp; Table</code></td><td>Target schema (default <code>public</code>) and table.</td><td>Yes</td></tr>
              <tr><td><code>Order direction</code></td><td>ASC / DESC for SELECT results.</td><td>No</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>Source rows</strong> — SELECT records to drive the workflow.</li>
            <li><strong>Persist extractions</strong> — INSERT structured data (e.g. invoice fields) into a table.</li>
            <li><strong>Sync</strong> — UPDATE existing rows as documents are processed.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use SSL for managed databases (DigitalOcean, RDS, etc.).</li>
            <li>Limit SELECT result size to keep runs fast.</li>
          </ul>
        </div>
      </div>
    `,
  },
};
